import React, { createContext, useCallback, useContext, useState } from 'react';
import type { AppThemeMode } from '@styles/theme';

const STORAGE_KEY = 'weavr_theme_mode';

interface ThemeModeContextValue {
    mode: AppThemeMode;
    toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
    mode: 'light',
    toggleMode: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [mode, setMode] = useState<AppThemeMode>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);

            return saved === 'dark' ? 'dark' : 'light';
        } catch {
            return 'light';
        }
    });

    const toggleMode = useCallback(() => {
        setMode(prev => {
            const next: AppThemeMode = prev === 'light' ? 'dark' : 'light';
            try {
                localStorage.setItem(STORAGE_KEY, next);
            } catch {
                // ignore storage errors
            }

            return next;
        });
    }, []);

    return (
        <ThemeModeContext.Provider value={{ mode, toggleMode }}>
            {children}
        </ThemeModeContext.Provider>
    );
};
